import { useState, useEffect } from "react";
import { getMatch, transformMatchData } from "../../../../services/minute-a-minute/getMatch";
import { getPreview, transformPreviewData, getHeadToHeadStats, getHeadToHeadMatches, getTopScorers, getCalendar } from "../../../../services/minute-a-minute/getPreview";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import Calendar from "./calendar/calendar";
import PreviousEncounters from "./previousEncounters/previousEncounters";
import Goalscorers from "./goalscorers/goalscorers";
import Loading from "../../../loading/loading";
import "./preview.sass";

const PREVIEW_TABS = [
  {
    id: "calendar",
    label: "CALENDARIO",
    component: Calendar,
  },
  {
    id: "previousEncounters",
    label: "ENFRENTAMIENTOS PREVIOS",
    component: PreviousEncounters,
  },
  {
    id: "goalscorers",
    label: "MÁXIMOS GOLEADORES",
    component: Goalscorers,
  },
];

export default function Preview({
  matchId = null,
  homeTeam,
  awayTeam,
  statisticsConfig,
  type,
  refresh = false,
  country = null,
  ...data
}) {
  const [activeTab, setActiveTab] = useState("calendar");
  const [loading, setLoading] = useState(!!matchId);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [matchData, setMatchData] = useState(null);

  // Función para obtener datos con o sin loading
  const fetchPreviewData = async (showLoading = true) => {
    if (!matchId) {
      setError("No se proporcionó el ID del partido");
      setLoading(false);
      return;
    }

    try {
      if (showLoading) setLoading(true);

      // Hacer ambas peticiones en paralelo
      const [matchResponse, previewResponse] = await Promise.all([
        getMatch(matchId, country),
        getPreview(matchId, country),
      ]);

      const transformedMatch = transformMatchData(matchResponse);
      const transformedPreview = transformPreviewData(previewResponse);

      setMatchData(transformedMatch);
      setPreviewData(transformedPreview);
      setError(null);
    } catch (err) {
      console.error("Error cargando datos de preview:", err);
      setError(err.message || "Error cargando datos de preview");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Usar hook de auto-refresh si refresh es true
  useAutoRefresh(refresh, fetchPreviewData, [matchId, refresh, country]);

  // Cargar datos iniciales si refresh es false
  useEffect(() => {
    if (!refresh) {
      fetchPreviewData(true);
    }
  }, [matchId, refresh, country]);

  const activeTabConfig = PREVIEW_TABS.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabConfig?.component;

  if (loading) {
    return (
      <div className="preview-container">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="preview-container">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="preview-container">
      {/* Navegación de tabs */}
      <nav className="preview-tabs-navigation">
        {PREVIEW_TABS.map(tab => (
          <button
            key={tab.id}
            className={`preview-tab-button ${activeTab === tab.id ? "preview-tab-button--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Contenido de la tab activa */}
      <div className="preview-tabs-content" key={activeTab}>
        {ActiveComponent && (
          activeTab === "calendar" ? (
            <Calendar 
              homeCalendarData={getCalendar(previewData, "home")}
              awayCalendarData={getCalendar(previewData, "away")}
              homeTeamName={matchData?.homeTeamInfo?.name}
              awayTeamName={matchData?.awayTeamInfo?.name}
            />
          ) : activeTab === "previousEncounters" ? (
            <PreviousEncounters 
              headToHeadStats={getHeadToHeadStats(previewData)}
              headToHeadMatches={getHeadToHeadMatches(previewData)}
              homeTeamName={matchData?.homeTeamInfo?.name || "Equipo Local"}
              awayTeamName={matchData?.awayTeamInfo?.name || "Equipo Visitante"}
            />
          ) : activeTab === "goalscorers" ? (
            <Goalscorers 
              homeScorers={getTopScorers(previewData, "home")}
              awayScorers={getTopScorers(previewData, "away")}
              homeTeamName={matchData?.homeTeamInfo?.name || "Equipo Local"}
              awayTeamName={matchData?.awayTeamInfo?.name || "Equipo Visitante"}
            />
          ) : (
            <ActiveComponent
              homeTeam={homeTeam}
              awayTeam={awayTeam}
              statisticsConfig={statisticsConfig}
              type={type}
              refresh={refresh}
              {...data}
            />
          )
        )}
      </div>
    </div>
  );
}
